import React, { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import { ArrowRight, Check } from "lucide-react";
import { IMaskInput } from "react-imask";

const CONTACT_API =
  process.env.REACT_APP_CONTACT_API_URL ||
  "https://d5d5m76riju0rqfm5tau.6brbn2wz.apigw.yandexcloud.net/api/contact";

export default function BookingSection({ onSuccess }) {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    consent: false,
  });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Пожалуйста, укажите ваше имя.");
      return;
    }
    const cleanPhone = form.phone.replace(/\D/g, "");
    if (cleanPhone.length < 11) {
      toast.error("Пожалуйста, введите полный номер телефона.");
      return;
    }

    if (!form.consent) {
      toast.error("Необходимо согласие на обработку персональных данных.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(CONTACT_API, {
        name: form.name.trim(),
        phone: form.phone.trim(),
      });
      setForm({ name: "", phone: "", consent: false });
      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      toast.error("Что-то пошло не так. Попробуйте снова.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="entradas"
      className="relative py-16 lg:py-24 overflow-hidden"
      data-testid="booking-section"
    >
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse at 20% 80%, rgba(229,115,36,0.18) 0%, rgba(8,6,5,0) 55%)",
        }}
      />
      <div className="container-custom relative grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1 }}
          className="lg:col-span-6"
        >
          <div className="eyebrow mb-5">· Получить скидку 20%</div>
          <h2
            className="font-serif font-light leading-[0.95] tracking-tight"
            style={{ fontSize: "clamp(2.4rem, 5.4vw, 4.4rem)", color: "#F2E9DE" }}
            data-testid="booking-title"
          >
            Промокод
            <br />
            <em style={{ color: "#E57324" }}>на скидку 20%</em>
          </h2>
          <p className="mt-6 text-base lg:text-lg leading-relaxed text-[#A99F96] max-w-lg">
            Оставьте контакты и получите персональный код на скидку 20%.
          </p>

          <div className="mt-8 flex items-center gap-4 text-[#A99F96]">
            <span className="text-[#E57324] text-xl">✦</span>
            <span className="text-sm tracking-[0.2em] uppercase">
              Билеты со скидкой
            </span>
          </div>
        </motion.div>

        <motion.form
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1, delay: 0.2 }}
          onSubmit={submit}
          className="lg:col-span-6 flex flex-col gap-7 p-7 md:p-10 border border-[#F2E9DE]/10"
          style={{ background: "rgba(18,15,13,0.5)", backdropFilter: "blur(10px)" }}
          data-testid="booking-form"
        >
          <div>
            <label className="eyebrow block mb-3">
              Имя <span style={{ color: "#E57324" }}>*</span>
            </label>
            <input
              type="text"
              className="input-line"
              placeholder="Ваше полное имя"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="booking-input-name"
              required
            />
          </div>
          <div>
            <label className="eyebrow block mb-3">
              Телефон <span style={{ color: "#E57324" }}>*</span>
            </label>
            <IMaskInput
              mask="+7 (000) 000-00-00"
              value={form.phone}
              onAccept={(value) => setForm((prev) => ({ ...prev, phone: value }))}
              onFocus={() => {
                if (!form.phone || form.phone === "") {
                  setForm((prev) => ({ ...prev, phone: "+7 " }));
                }
              }}
              onBlur={() => {
                if (form.phone === "+7 " || form.phone === "+7") {
                  setForm((prev) => ({ ...prev, phone: "" }));
                }
              }}
              className="input-line"
              placeholder="+7 (____) ___-___-___-___"
              data-testid="booking-input-phone"
              required
              autoComplete="tel"
            />
          </div>

          <label
            className="flex items-start gap-3 cursor-pointer group select-none"
            data-testid="booking-consent-label"
          >
            <span
              className="mt-0.5 flex-none w-4 h-4 border border-[#A99F96] flex items-center justify-center transition-all group-hover:border-[#E57324]"
              style={{
                background: form.consent ? "#E57324" : "transparent",
                borderColor: form.consent ? "#E57324" : undefined,
              }}
            >
              {form.consent && <Check size={12} className="text-[#080605]" strokeWidth={3} />}
            </span>
            <input
              type="checkbox"
              className="sr-only"
              checked={form.consent}
              onChange={(e) => setForm({ ...form, consent: e.target.checked })}
              data-testid="booking-input-consent"
            />
            <span className="text-[0.72rem] leading-relaxed text-[#A99F96]">
              Я прочитал(а) и даю добровольное{' '}
              <a
                href="/consent"
                className="text-[#E57324] hover:text-[#FFB84D] transition-colors underline underline-offset-2"
              >
                Согласие на обработку персональных данных
              </a>{' '}
              в соответствии с{' '}
              <a
                href="/privacy"
                className="text-[#E57324] hover:text-[#FFB84D] transition-colors underline underline-offset-2"
              >
                Политикой конфиденциальности
              </a>
              . Этот запрос не
              является покупкой или бронью билетов. Организатор мероприятия —
              иммерсивный театр-ресторан «Наблюдатель». Билеты продаются через
              Яндекс.Афишу.
            </span>
          </label>

          <button
            type="submit"
            className="btn-primary self-start disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={loading}
            data-testid="booking-submit"
          >
            {loading ? "Отправка…" : "Получить код"} <ArrowRight size={16} />
          </button>
        </motion.form>
      </div>
    </section>
  );
}
